import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAlertStudents } from '../hooks/useAlertStudents';
import ImageWithBasePath from '../../../../components/Common/imageWithBasePath';
import { StudentAlert } from '../../../../types/absenceTypes';
import SkeletonLoader from '../../../../components/Common/SkeletonLoader';
import Badge from '../../../../components/Common/ui/Badge';
import ImageWithoutBasePath from 'components/Common/imageWithoutBasePath';

interface StudentAlertsPreviewProps {
  alerts: StudentAlert[];
  loading: boolean;
}

const StudentAlertsPreview: React.FC<StudentAlertsPreviewProps> = ({ alerts, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <SkeletonLoader type="text" width="150px" height="24px" variant="large" />
          <SkeletonLoader type="button" width="80px" height="32px" />
        </div>
        <div className="card-body skeleton-list-item">
          {[1, 2, 3].map((i) => (
            <div key={i} className="d-flex align-items-center border-bottom pb-3 mb-3" style={{ '--item-index': i } as React.CSSProperties}>
              <div className="mr-4">
                <SkeletonLoader type="circle" width="40px" height="40px" index={i} />
              </div>
              <div className="flex-grow-1">
                <SkeletonLoader type="text" width="120px" height="18px" className="mb-1" index={i} />
                <SkeletonLoader type="text" width="80px" height="14px" variant="small" index={i} />
              </div>
              <div className="text-right">
                <SkeletonLoader type="badge" width="60px" height="24px" className="mb-1" index={i} />
                <SkeletonLoader type="progress" width="100px" height="5px" index={i} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title">{t('high_risk_students')}</h5>
        <Link to="/absences/alerts" className="btn btn-sm btn-primary">
          {t('view_all')}
        </Link>
      </div>
      <div className="card-body">
        {alerts.length === 0 ? (
          <div className="text-center py-4">
            <i className="ti ti-circle-check text-success ti-3x mb-3"></i>
            <p>{t('no_students_at_risk')}</p>
          </div>
        ) : (
          <div>
            {alerts.map((alert) => (
              <div
                key={alert.student.id}
                className="d-flex align-items-center border-bottom pb-3 mb-3"
              >
                <div className="avatar me-3">
                  <ImageWithoutBasePath
                    src={alert.student.user.photo || 'assets/img/profiles/avatar-01.jpg'}
                    alt={`${alert.student.user.firstName} ${alert.student.user.lastName}`}
                    className="rounded-circle"
                    width={50}
                  />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-0">
                    {alert.student.user.firstName} {alert.student.user.lastName}
                  </h6>
                  <small className="text-muted">{alert.student.class?.name}</small>
                </div>
                <div className="text-right">
                  <Badge
                    variant={alert.riskLevel === 'critical' ? 'danger' : 'warning'}
                    text={alert.riskLevel === 'critical' ? 'critical' : 'warning'}
                    className="p-2"
                  />
                  <div className="progress mt-1" style={{ height: '5px', width: '100px' }}>
                    <div
                      className={`progress-bar ${
                        alert.riskLevel === 'critical' ? 'bg-danger' : 'bg-warning'
                      }`}
                      role="progressbar"
                      style={{ width: `${alert.percentageOfThreshold * 100}%` }}
                      aria-valuenow={alert.percentageOfThreshold * 100}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <small>{Math.round(alert.percentageOfThreshold * 100)}%</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentAlertsPreview;
